import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import katex, { type KatexOptions } from 'katex';
import { tex2html } from './mathjax';
import { getScrollParent } from './utils';

export type MathematicsNodeViewConfig = {
    editor: Editor;
    node: PMNode;
    getPos: () => number | undefined;
    dom: HTMLElement;
    renderTarget: HTMLElement;
    isMathJax: boolean;
    katexOptions?: KatexOptions;
    inline: boolean;
    errorClass: 'inline-math-error' | 'block-math-error';
};

// Creates a signature for the content of the node to determine if it needs to be rendered again.
function getContentSignature(node: PMNode): string {
    return `${node.attrs.latex}\0${node.attrs.mathml ?? ''}`;
}

function getRenderCacheKey(node: PMNode, isMathJax: boolean, inline: boolean): string {
    return `${isMathJax ? 'mj' : 'kx'}\0${inline ? 'i' : 'b'}\0${getContentSignature(node)}`;
}

// Survives ProseMirror node-view recreation when search decorations change.
const mathRenderCache = new Map<string, string>();

// Checks if the element has rendered output by looking for MathJax or KaTeX containers.
function hasRenderedOutput(element: HTMLElement): boolean {
    return Boolean(element.querySelector('mjx-container, .katex'));
}

/**
 * Common Mathematics view logic for both inline and block math nodes.
 *
 * @param config
 * @returns A Mathematics node view object with the following properties:
 * - dom: The DOM element representing the math node.
 * - ignoreMutation: A function that returns true if the mutation should be ignored.
 * - update: A function that updates the math node.
 * - destroy: A function that destroys the math node.
 */
export function createMathematicsNodeView(config: MathematicsNodeViewConfig) {
    const { editor, getPos, dom, renderTarget, isMathJax, katexOptions, inline, errorClass } = config;

    let node = config.node;
    let renderedSignature = '';
    let intersectionObserver: IntersectionObserver | null = null;

    const disconnectObserver = () => {
        intersectionObserver?.disconnect();
        intersectionObserver = null;
    };

    const isAlreadyRendered = () => renderedSignature === getContentSignature(node) && hasRenderedOutput(renderTarget);

    const saveRenderedOutput = () => {
        mathRenderCache.set(getRenderCacheKey(node, isMathJax, inline), renderTarget.innerHTML);
    };

    const restoreRenderedOutput = (): boolean => {
        const cached = mathRenderCache.get(getRenderCacheKey(node, isMathJax, inline));
        if (!cached) return false;
        renderTarget.innerHTML = cached;
        renderedSignature = getContentSignature(node);
        dom.classList.remove(errorClass);
        return true;
    };

    async function runMathJaxRender() {
        try {
            const isMathMl = Boolean(node.attrs.mathml);
            const contentToUse = isMathMl ? node.attrs.mathml : node.attrs.latex;
            const renderedTex = await tex2html(contentToUse, {
                inputFormat: isMathMl ? 'mathml' : 'tex',
                inline,
                css: true,
            });
            renderTarget.innerHTML = `<style>${renderedTex.css}</style>${renderedTex.html}`;
            dom.classList.remove(errorClass);
            renderedSignature = getContentSignature(node);
            saveRenderedOutput();
        } catch {
            renderTarget.textContent = node.attrs.latex;
            dom.classList.add(errorClass);
            renderedSignature = '';
        }
    }

    function renderWhenVisible() {
        if (isAlreadyRendered() || restoreRenderedOutput()) {
            return;
        }

        if (!isMathJax) {
            try {
                katex.render(node.attrs.latex, renderTarget, katexOptions);
                dom.classList.remove(errorClass);
                renderedSignature = getContentSignature(node);
                saveRenderedOutput();
            } catch {
                renderTarget.textContent = node.attrs.latex;
                dom.classList.add(errorClass);
                renderedSignature = '';
            }
            return;
        }

        if (!hasRenderedOutput(dom)) {
            renderTarget.textContent = node.attrs.latex;
        }

        let root: Element | undefined;
        try {
            root = getScrollParent(editor.view.dom) ?? undefined;
        } catch {
            // ignore
        }

        disconnectObserver();
        intersectionObserver = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                if (!entry?.isIntersecting || !intersectionObserver) {
                    return;
                }
                disconnectObserver();
                void runMathJaxRender();
            },
            { root, rootMargin: '100%', threshold: 0 },
        );
        intersectionObserver.observe(dom);
    }

    const handleClick = (event: MouseEvent) => {
        if (!editor.isEditable) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const pos = getPos();
        if (pos == null) {
            return;
        }
        editor.commands.decomposeMath({ pos, cursorAt: 'start' });
        editor.view.focus();
    };

    // Always attach; handleClick guards on editor.isEditable at click time.
    // NodeViews are not recreated when setEditable flips (e.g. Resume after view-only load).
    const syncEditableClass = () => {
        dom.classList.toggle('tiptap-mathematics-render--editable', editor.isEditable);
    };
    syncEditableClass();
    editor.on('update', syncEditableClass);
    dom.addEventListener('mousedown', handleClick);

    renderWhenVisible();

    return {
        dom,
        ignoreMutation: () => true,
        update(updatedNode: PMNode) {
            if (updatedNode.type !== node.type) {
                return false;
            }

            if (updatedNode.eq(node)) {
                return true;
            }

            const previousSignature = getContentSignature(node);
            node = updatedNode;
            dom.setAttribute('data-latex', node.attrs.latex);

            if (getContentSignature(node) === previousSignature) {
                return true;
            }

            renderedSignature = '';
            disconnectObserver();
            renderWhenVisible();
            return true;
        },
        destroy() {
            disconnectObserver();
            editor.off('update', syncEditableClass);
            dom.removeEventListener('mousedown', handleClick);
        },
    };
}
